import {
  CellId,
  CellMention,
  CellType,
  DataConnectionId,
  DataSourceTableId,
  DataframeMention,
  FileId,
  FileMention,
  GroupId,
  GroupMention,
  HexId,
  HexMention,
  HexType,
  MentionElement as MentionElementType,
  MentionedDataframeName,
  SemanticDatasetId,
  SemanticDatasetMention,
  SemanticDatasetName,
  SemanticProjectId,
  SemanticViewId,
  SemanticViewMention,
  SemanticViewName,
  TableMention,
  UserId,
  UserMention,
  assertNever,
  guardNever,
  isImageFileName,
  semanticDatasetTitleString,
  semanticViewTitleString,
  typedKeyGroupBy,
  typedObjectEntries,
  uuid,
} from "@hex/common";
import {
  PlateEditor,
  PlateElement,
  PlateElementProps,
  PlatePlugin,
  PlatePluginComponent,
  Value,
  insertNodes,
} from "@udecode/plate-common";
import {
  ELEMENT_MENTION,
  ELEMENT_MENTION_INPUT,
  MentionPlugin,
  type TMentionItemBase,
  createMentionPlugin as createMentionPlatePlugin,
  getMentionOnSelectItem,
} from "@udecode/plate-mention";
import { capitalize, last } from "lodash";
import React, { forwardRef, useCallback, useEffect, useState } from "react";
import styled from "styled-components";

import { StatusFragment } from "../../app/ProjectLabels.generated";
import { cellTypeToIconType } from "../../cell/CellTypeIcon";
import { StatusLabel } from "../../common/labels/StatusLabel";
import { Heading } from "../../Heading";
import {
  ComponentIcon,
  DataframeIcon,
  DocsIcon,
  PictureIcon,
  ProjectIcon,
  SemanticDatasetIcon,
  SemanticViewIcon,
  WarehouseTableIcon,
} from "../../icons/CustomIcons.js";
import { GroupAvatar } from "../../user/GroupAvatar.js";
import { UserAvatar } from "../../user/UserAvatar";
import { MentionElementRenderer } from "../elementRenderers";

import {
  DataframeMentionDetails,
  HexMentionDetails,
  PinnedTableIcon,
  TableMentionDetails,
} from "./MentionDetails";
import {
  type MentionSuggestionSection,
  flattenMentionSuggestionSections,
} from "./mentionSearchSectionUtils";
import { RichTextCombobox, RichTextComboboxItem } from "./RichTextCombobox";
import { useRichTextMentionContext } from "./RichTextMentionContext";
import {
  SemanticDatasetMentionDetails,
  SemanticViewMentionDetails,
} from "./SemanticResourceMentionDetails";

const MENTION_TYPE_PLURALS: Record<RichTextMentionData["mentionType"], string> =
  {
    hex: "Projects",
    table: "Tables",
    dataframe: "Dataframes",
    user: "Users",
    group: "Groups",
    cell: "Cells",
    semantic_dataset: "Semantic Models",
    semantic_view: "Semantic Views",
    file: "Files",
  };

// ----------------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------------

export type RichTextMentionData =
  | RichTextMentionData.User
  | RichTextMentionData.Group
  | RichTextMentionData.Table
  | RichTextMentionData.Dataframe
  | RichTextMentionData.Hex
  | RichTextMentionData.Cell
  | RichTextMentionData.SemanticDataset
  | RichTextMentionData.SemanticView
  | RichTextMentionData.File;

export namespace RichTextMentionData {
  export interface User {
    readonly id: UserId;
    readonly name: string | null;
    readonly email: string;
    readonly imageUrl: string | null;
    readonly mentionType: "user";
  }

  export interface Group {
    readonly id: GroupId;
    readonly name: string;
    readonly mentionType: "group";
  }

  export interface Table {
    readonly tableName: string;
    readonly schemaName: string;
    readonly databaseName: string | null;
    readonly mentionType: "table";
    readonly id: DataSourceTableId;
    readonly connectionId: DataConnectionId;
    readonly description: string | null;
    readonly pinned: boolean;
    readonly status: StatusFragment | null;
  }

  export interface Dataframe {
    readonly mentionType: "dataframe";
    readonly id: string;
    readonly name: MentionedDataframeName;
    readonly connectionId: DataConnectionId | null;
    readonly columns: string[];
    readonly isPlanned?: boolean; // Flag for not-yet-executed cells
  }

  export interface Hex {
    readonly mentionType: "hex";
    readonly id: HexId;
    readonly hexType: HexType;
    readonly title: string;
  }

  export interface Cell {
    readonly mentionType: "cell";
    readonly id: CellId;
    readonly cellType: CellType;
    readonly name: string;
  }

  export interface SemanticDataset {
    readonly mentionType: "semantic_dataset";
    readonly id: SemanticDatasetId;
    readonly name: SemanticDatasetName;
    readonly title?: string;
    readonly semanticProjectId: SemanticProjectId;
  }

  export interface SemanticView {
    readonly mentionType: "semantic_view";
    readonly id: SemanticViewId;
    readonly name: SemanticViewName;
    readonly title?: string;
    readonly semanticProjectId: SemanticProjectId;
  }

  export interface File {
    readonly mentionType: "file";
    readonly id: FileId;
    readonly fileName: string;
  }
}

// ----------------------------------------------------------------------------
// Plugin
// ----------------------------------------------------------------------------

type RichTextMentionItem = RichTextMentionData & TMentionItemBase;

type CreateMentionNode<T extends MentionElementType> = Omit<T, "children"> & {
  value: string;
};

export const createMentionNode = (
  item: RichTextMentionData,
):
  | CreateMentionNode<GroupMention>
  | CreateMentionNode<UserMention>
  | CreateMentionNode<TableMention>
  | CreateMentionNode<DataframeMention>
  | CreateMentionNode<HexMention>
  | CreateMentionNode<CellMention>
  | CreateMentionNode<SemanticDatasetMention>
  | CreateMentionNode<SemanticViewMention>
  | CreateMentionNode<FileMention> => {
  const { mentionType } = item;
  if (mentionType === "user") {
    return {
      previewText: item.name || item.email,
      userId: item.id,
      type: ELEMENT_MENTION,
      mentionType: item.mentionType,
      value: item.id,
      id: uuid(),
    };
  } else if (mentionType === "group") {
    return {
      previewText: item.name,
      groupId: item.id,
      type: ELEMENT_MENTION,
      mentionType: item.mentionType,
      value: item.id,
      id: uuid(),
    };
  } else if (mentionType === "table") {
    return {
      previewText: `${item.databaseName ? item.databaseName + "." : ""}${
        item.schemaName
      }.${item.tableName}`,
      type: ELEMENT_MENTION,
      mentionType: item.mentionType,
      table: item.tableName,
      schema: item.schemaName,
      database: item.databaseName,
      value: item.id,
      tableId: item.id,
      id: uuid(),
      connectionId: item.connectionId,
    };
  } else if (mentionType === "dataframe") {
    return {
      previewText: item.name,
      type: ELEMENT_MENTION,
      mentionType: item.mentionType,
      value: item.name,
      id: uuid(),
      name: item.name,
      connectionId: item.connectionId,
    };
  } else if (mentionType === "hex") {
    return {
      previewText: item.title,
      type: ELEMENT_MENTION,
      mentionType: item.mentionType,
      id: uuid(),
      hexId: item.id,
      value: item.id,
      hexType: item.hexType,
    };
  } else if (mentionType === "cell") {
    return {
      previewText: item.name,
      type: ELEMENT_MENTION,
      mentionType: item.mentionType,
      id: uuid(),
      value: item.id,
      cellId: item.id,
      name: item.name,
      cellType: item.cellType,
    };
  } else if (mentionType === "semantic_dataset") {
    return {
      previewText: semanticDatasetTitleString(item),
      type: ELEMENT_MENTION,
      mentionType: item.mentionType,
      id: uuid(),
      value: item.id,
      semanticDatasetId: item.id,
      name: item.name,
      semanticProjectId: item.semanticProjectId,
    };
  } else if (mentionType === "semantic_view") {
    return {
      previewText: semanticViewTitleString(item),
      type: ELEMENT_MENTION,
      mentionType: item.mentionType,
      id: uuid(),
      value: item.id,
      semanticViewId: item.id,
      name: item.name,
      semanticProjectId: item.semanticProjectId,
    };
  } else if (mentionType === "file") {
    return {
      previewText: item.fileName,
      type: ELEMENT_MENTION,
      mentionType: item.mentionType,
      id: uuid(),
      value: item.id,
      fileId: item.id,
      fileName: item.fileName,
    };
  } else {
    assertNever(mentionType, mentionType);
  }
};

export function createMentionPlugin(): PlatePlugin<any, any, any> {
  return createMentionPlatePlugin<MentionPlugin<RichTextMentionItem>>({
    options: { createMentionNode },
  });
}

// ----------------------------------------------------------------------------
// View Element
// ----------------------------------------------------------------------------

const MentionElement: PlatePluginComponent = (props) => (
  <MentionElementRenderer {...props} element={props.element} />
);

// ----------------------------------------------------------------------------
// Input Element
// ----------------------------------------------------------------------------

const onSelectMentionItem = getMentionOnSelectItem();

const SchemaLabel = styled.span`
  color: ${({ theme }) => theme.fontColor.MUTED};
`;

const RightIconContainer = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const MentionComboboxItem = React.memo(function MentionComboboxItem({
  editor,
  item,
  onSelectSideEffect,
  search,
}: {
  editor: PlateEditor<Value>;
  item: RichTextMentionData;
  onSelectSideEffect?: (
    editor: PlateEditor<Value>,
    item: RichTextMentionData,
  ) => void;
  search: string;
}) {
  const { condensedTableMentions } = useRichTextMentionContext();
  const mentionType = item.mentionType;

  const onItemChosen = useCallback(() => {
    onSelectSideEffect?.(editor, item);
    onSelectMentionItem(editor, { text: item.id, ...item }, search);
    // Plate doesn't handle click and edit immediately after a non-text element well, so we
    // insert a space after the mention
    insertNodes(editor, { text: " " });
  }, [editor, item, onSelectSideEffect, search]);

  let text: JSX.Element | string = "";
  let icon: JSX.Element;
  let label: JSX.Element | string | null = null;
  let isPlanned: boolean | undefined = undefined;

  if (mentionType === "user") {
    text = item.name || item.email;
    label = item.email;
    icon = (
      <UserAvatar
        imageUrl={item.imageUrl ?? undefined}
        name={item.name ?? undefined}
        size={20}
        tooltip={false}
      />
    );
  } else if (mentionType === "group") {
    text = item.name;
    icon = <GroupAvatar name={item.name} />;
  } else if (mentionType === "table") {
    if (condensedTableMentions) {
      text = item.tableName;
    } else {
      const pathSegments =
        `${item.databaseName ? item.databaseName + "." : ""}${
          item.schemaName
        }.${item.tableName}`.split(".");
      text = (
        <>
          {pathSegments.slice(0, -1).map((t: string, i: number) => (
            <SchemaLabel key={i}>{t}/</SchemaLabel>
          ))}
          {last(pathSegments)}
        </>
      );
    }
    label = (
      <RightIconContainer>
        {item.pinned && <PinnedTableIcon />}
        {item.status && (
          <StatusLabel condensed={true} minimal={true} status={item.status} />
        )}
      </RightIconContainer>
    );
    icon = <WarehouseTableIcon />;
  } else if (mentionType === "dataframe") {
    text = item.name;
    icon = <DataframeIcon />;
    isPlanned = item.isPlanned;
  } else if (mentionType === "hex") {
    text = item.title;
    icon =
      item.hexType === HexType.PROJECT ? <ProjectIcon /> : <ComponentIcon />;
  } else if (mentionType === "cell") {
    text = item.name;
    const CellTypeIcon = cellTypeToIconType(item.cellType);
    icon = <CellTypeIcon />;
  } else if (mentionType === "semantic_dataset") {
    text = semanticDatasetTitleString(item);
    icon = <SemanticDatasetIcon />;
  } else if (mentionType === "semantic_view") {
    text = semanticViewTitleString(item);
    icon = <SemanticViewIcon />;
  } else if (mentionType === "file") {
    text = item.fileName;
    icon = isImageFileName(item.fileName) ? <PictureIcon /> : <DocsIcon />;
  } else {
    // We could run into this case at runtime if there are other mention plugins
    // because plate is bad and merges all items for all combobox plugins into
    // a single list in its store.
    // This callback should be responsible for handling all mention types defined
    // in this plugin which will be caught at compile time by the above assertNever
    // but should handle items from other mentions gracefully (via noop).
    guardNever(mentionType, mentionType);
    return null;
  }

  return (
    <RichTextComboboxItem
      icon={icon}
      isMonospace={
        item.mentionType === "dataframe" ||
        item.mentionType === "table" ||
        item.mentionType === "semantic_dataset"
      }
      isPlanned={isPlanned}
      label={label}
      text={text}
      value={item.id}
      onItemChosen={onItemChosen}
    />
  );
});

function groupSuggestions(
  suggestions: readonly RichTextMentionData[],
): MentionSuggestionSection[] {
  if (suggestions == null) return [];
  const grouped = typedKeyGroupBy(suggestions, "mentionType");
  const entries = typedObjectEntries(grouped);
  const sanitizedEntries: MentionSuggestionSection[] = [];
  for (const entry of entries) {
    if (entry == null) continue;
    const [k, v] = entry;
    if (v == null) continue;
    sanitizedEntries.push({
      key: k,
      title:
        MENTION_TYPE_PLURALS[k as RichTextMentionData["mentionType"]] ||
        `${capitalize(k)}s`,
      items: v,
    });
  }
  return sanitizedEntries;
}

export const MentionInputElement = forwardRef<
  HTMLDivElement,
  PlateElementProps
>(function MentionInputElement({ className, ...props }, ref) {
  const { children, editor, element } = props;
  const [search, setSearch] = useState("");
  const {
    groupSuggestionSections,
    onMentionSearch,
    onSelectSideEffect,
    shouldGroupSuggestions,
    showInDataBrowser,
  } = useRichTextMentionContext();
  const [suggestions, setSuggestions] =
    useState<readonly RichTextMentionData[]>();

  useEffect(() => {
    if (search == null) {
      return;
    }
    let cancelled = false;
    void Promise.all(onMentionSearch.map((cb) => cb(search))).then(
      (results) => {
        const flattened = results.flat();
        const isDataSearch = !!flattened.find(
          (i) => i.mentionType === "dataframe" || i.mentionType === "table",
        );

        if (!cancelled) {
          if (groupSuggestionSections != null) {
            setSuggestions(
              flattenMentionSuggestionSections(
                groupSuggestionSections(flattened),
              ),
            );
          } else {
            const projectResults = flattened.filter(
              (r) => r.mentionType === "hex",
            );
            const nonProjectResults = flattened.filter(
              (r) => r.mentionType !== "hex",
            );
            if (projectResults.length > 0) {
              // Special case for semantic authoring agent where we want to make sure projects are surfaced
              setSuggestions([
                ...nonProjectResults.slice(0, 10),
                ...projectResults.slice(0, 10),
              ]);
            } else {
              setSuggestions(
                isDataSearch ? flattened.slice(0, 20) : flattened.slice(0, 5),
              );
            }
          }
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [groupSuggestionSections, onMentionSearch, search]);

  const renderDetails = useCallback(
    (itemValue: string) => {
      const mentionItem = (suggestions ?? []).find(
        (item) => item.id === itemValue,
      );
      if (mentionItem) {
        if (mentionItem.mentionType === "table") {
          return (
            <TableMentionDetails
              mention={mentionItem}
              showInDataBrowser={showInDataBrowser}
            />
          );
        } else if (mentionItem.mentionType === "dataframe") {
          return <DataframeMentionDetails mention={mentionItem} />;
        } else if (mentionItem.mentionType === "hex") {
          return <HexMentionDetails mention={mentionItem} />;
        } else if (mentionItem.mentionType === "semantic_dataset") {
          return <SemanticDatasetMentionDetails mention={mentionItem} />;
        } else if (mentionItem.mentionType === "semantic_view") {
          return <SemanticViewMentionDetails mention={mentionItem} />;
        }
      }
      return null;
    },
    [showInDataBrowser, suggestions],
  );

  return (
    <PlateElement
      ref={ref}
      as="span"
      data-slate-value={element.value}
      {...props}
    >
      <RichTextCombobox
        dataLoaded={suggestions != null}
        element={element}
        setValue={setSearch}
        trigger="@"
        value={search}
        onRenderDetails={renderDetails}
      >
        {shouldGroupSuggestions
          ? (groupSuggestionSections != null
              ? groupSuggestionSections(suggestions ?? [])
              : groupSuggestions(suggestions ?? [])
            ).map((section) => (
              <React.Fragment key={section.key}>
                <StyledHeading renderAs="h5">{section.title}</StyledHeading>
                {section.items.map((suggestion) => (
                  <MentionComboboxItem
                    key={suggestion.id}
                    editor={editor}
                    item={suggestion}
                    search={search}
                    onSelectSideEffect={onSelectSideEffect}
                  />
                ))}
              </React.Fragment>
            ))
          : (suggestions ?? []).map((suggestion) => (
              <MentionComboboxItem
                key={suggestion.id}
                editor={editor}
                item={suggestion}
                search={search}
                onSelectSideEffect={onSelectSideEffect}
              />
            ))}
      </RichTextCombobox>
      {children}
    </PlateElement>
  );
});

const StyledHeading = styled(Heading)`
  padding: 3px 1px;
`;

// ----------------------------------------------------------------------------
// Components
// ----------------------------------------------------------------------------

export function createMentionPluginComponents(): Record<
  string,
  PlatePluginComponent
> {
  return {
    [ELEMENT_MENTION]: MentionElement,
    [ELEMENT_MENTION_INPUT]: MentionInputElement,
  };
}
